import { CommonModule } from '@angular/common'
import { Component, inject, Input, OnInit, ViewChild } from '@angular/core'
import { ReactiveFormsModule } from '@angular/forms'
import { EVENT } from '@app/analytics/analytics.cnst'
import { AnalyticsService } from '@app/analytics/analytics.service'
import { InfoButtonComponent } from '@app/cmp/info-button/info-button.component'
import { ICON } from '@app/cnst/icons.cnst'
import { isIOSApp } from '@app/cnst/userDevice.cnst'
import { decorate, ErrorHandlerType, LoaderType } from '@app/decorators/decorators'
import { GhostDirective } from '@app/dir/ghost/ghost.directive'
import { ValuePickerModal } from '@app/modals/value-picker/value-picker.modal'
import { ValuePickerValue } from '@app/modals/value-picker/value-picker.model'
import { BaseModal } from '@app/pages/base.modal'
import { MdPipe } from '@app/pipes/md.pipe'
import {
  AppTrackingTransparencyService,
  AppTrackingTransparencyStatus,
} from '@app/srv/appTrackingTransparency.service'
import { ConsentService } from '@app/srv/consent.service'
import { Priority } from '@app/srv/popup.cnst'
import { PopupController } from '@app/srv/popup.controller'
import { getState } from '@app/srv/store.service'
import { tr } from '@app/srv/translation.util'
import {
  IonButton,
  IonCheckbox,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/angular/standalone'
import { _assert, _stringEnumValues } from '@naturalcycles/js-lib'
import { ConsentTerms, ConsentTermsItemToggleKey, LANG_DEFAULT } from '@naturalcycles/shared'
import { captureException } from '@sentry/browser'
import { ScrollFooterComponent } from '@src/app/cmp/scroll-footer/scroll-footer.component'
import { PrivacyTermsConsent } from '@src/typings/auth'
import { BehaviorSubject } from 'rxjs'

@Component({
  selector: 'page-auth-modal',
  templateUrl: './auth-terms.page.html',
  styleUrls: ['./auth-terms.page.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InfoButtonComponent,
    GhostDirective,
    MdPipe,
    ScrollFooterComponent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonButton,
    IonLabel,
    IonIcon,
    IonContent,
    IonList,
    IonItem,
    IonCheckbox,
    IonGrid,
    IonRow,
    IonCol,
  ],
})
export class AuthTermsPage extends BaseModal<PrivacyTermsConsent> implements OnInit {
  className = 'AuthTermsModal'

  private popupController = inject(PopupController)
  private analyticsService = inject(AnalyticsService)
  private consentService = inject(ConsentService)
  private appTrackingTransparencyService = inject(AppTrackingTransparencyService)

  @Input()
  public partnerEmail?: string

  @ViewChild(IonContent)
  public content!: IonContent

  public terms$: BehaviorSubject<ConsentTerms | undefined> = new BehaviorSubject<
    ConsentTerms | undefined
  >({} as ConsentTerms)

  public languageOptions: ValuePickerValue[] = []
  public selectedLanguage!: string | undefined
  public toggleMap: Record<ConsentTermsItemToggleKey, boolean> = {
    [ConsentTermsItemToggleKey.marketing]: true,
    [ConsentTermsItemToggleKey.socialMarketing]: true,
  }

  public ICON = ICON

  public ngOnInit(): void {
    const { userLocale } = getState()
    void this.fetchTerms(userLocale.supportedLanguage, this.partnerEmail)
  }

  public async selectLanguageModal(): Promise<void> {
    const modal = await this.popupController.presentModal(
      {
        component: ValuePickerModal,
        componentProps: {
          values: this.languageOptions,
          alignRight: true,
        },
        cssClass: 'modal--transparent',
      },
      'modal-valuePicker-cycles',
      Priority.IMMEDIATE,
    )
    const { data } = await modal.onDidDismiss()

    if (!data?.value) return
    if (data) {
      await this.fetchTerms(data.value)
      this.selectedLanguage = data.label
    }
  }

  public clickItemToggle(key: ConsentTermsItemToggleKey): void {
    this.toggleMap[key] = !this.toggleMap[key]
  }

  @decorate({
    loaderType: LoaderType.BLOCKING,
    errorHandlerType: ErrorHandlerType.DIALOG,
  })
  private async fetchTerms(lang?: string, partnerEmail?: string): Promise<void> {
    const terms = await this.consentService.fetchTerms(lang || LANG_DEFAULT, !!partnerEmail)

    // add email in terms text
    if (partnerEmail) {
      terms?.items.map(term => {
        term.text &&= term.text.replaceAll('{{email}}', partnerEmail)
      })
    }

    this.languageOptions = terms?.languageOptions as ValuePickerValue[]
    this.selectedLanguage = terms?.languageOptions.find(option => option.value === lang)?.label

    _stringEnumValues(ConsentTermsItemToggleKey).forEach(key => {
      const toggleInfo = terms?.items.find(item => item.toggleInfo?.key === key)?.toggleInfo
      if (toggleInfo) {
        this.toggleMap[key] = toggleInfo.defaultChecked
      } else if (
        // socialMarketing item is only shown for partners on iOS, no need to report
        !(isIOSApp && key === ConsentTermsItemToggleKey.socialMarketing && !this.partnerEmail)
      ) {
        captureException(new Error(`No auth terms toggle found for key: ${key}`))
      }
    })

    this.terms$.next(terms)
  }

  public async accept(): Promise<void> {
    const terms = this.terms$.getValue()
    _assert(terms, 'An error occurred while fetching terms and conditions')

    const fromATT = isIOSApp && !this.partnerEmail

    let marketingSocialConsent = this.toggleMap.socialMarketing
    let marketingSocialConsentItem = terms.items.find(
      item => item.toggleInfo?.key === ConsentTermsItemToggleKey.socialMarketing,
    )

    if (fromATT) {
      const {
        status,
        message = '',
        language,
      } = await this.appTrackingTransparencyService.requestPermission('AuthTerms')

      if (language) terms.language = language

      marketingSocialConsentItem = {
        key: 'Info.plist.NSUserTrackingUsageDescription',
        text: message,
      }

      marketingSocialConsent = status === AppTrackingTransparencyStatus.AUTHORIZED
    }

    void this.dismissModal({
      accepted: true,
      marketingConsent: this.toggleMap.marketing,
      marketingSocialConsent,
      marketingSocialConsentTo: marketingSocialConsentItem?.text,
      marketingSocialConsentKey: marketingSocialConsentItem?.key,
      marketingSocialConsentFromATT: fromATT,
      consentTermsLanguage: terms.language,
    })
  }

  public async decline(): Promise<void> {
    const texts = {
      message: tr('settings-healthData-revoke-body'),
      decline: tr('txt-im-sure'),
      cancel: tr('btn-cancel'),
    }

    const alert = await this.popupController.presentAlert(
      {
        header: tr('msg-areYouSure'),
        message: texts.message,
        cssClass: 'auth__terms__alert',
        buttons: [
          {
            text: texts.cancel,
            cssClass: 'auth__terms__cancel',
            htmlAttributes: {
              uid: 'authTermsAlert__cancel__button',
            },
            role: 'cancel',
          },
          {
            text: texts.decline,
            cssClass: 'auth__terms__ok',
            htmlAttributes: {
              uid: 'authTermsAlert__ok__button',
            },
            handler: () => {
              void this.analyticsService.trackEvent(EVENT.DECLINED_REGISTRATION_CONSENT)
            },
            role: 'decline',
          },
        ],
      },
      'alert-authTerms-decline',
      Priority.IMMEDIATE,
    )
    const { role } = await alert.onDidDismiss()
    if (role === 'decline') await this.dismissModal()
  }
}
